import { createFileRoute, useRouter } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Loading } from "@/Loading";
import { Dialog } from "@/Dialog";
import { useState } from "react";
import {
  useTicketDetail,
  useRefundTicket,
  useTicketList,
} from "~/services/customer/myPage";
import { TicketListItem } from "~/types/common";

export const Route = createFileRoute(
  "/customer/layout/user-subscription-detail"
)({
  component: UserSubscriptionDetail,
  validateSearch: (
    search: Record<string, unknown>,
  ): { subscriptionSeq: number; petSeq?: number } => ({
    subscriptionSeq: Number(search.subscriptionSeq) || 0,
    // 진입 화면(홈 등)에서 선택돼 있던 반려견 — 예약하기로 넘길 때 사용
    petSeq: Number(search.petSeq) || undefined,
  }),
});

function UserSubscriptionDetail() {
  const { history } = useRouter();
  const { subscriptionSeq, petSeq } = Route.useSearch();
  const purchaseTicketSeq = subscriptionSeq > 0 ? subscriptionSeq : null;
  const { data: ticketDetail, isLoading: isDetailLoading } =
    useTicketDetail(purchaseTicketSeq);
  const refundTicketMutation = useRefundTicket();

  // 사용 가능한 이용권 목록 — 여기에 있으면 예약에 쓸 수 있는 이용권이다.
  // (홈/이용권 조회 화면과 같은 쿼리라 대개 캐시로 처리된다)
  const { data: usableTickets = [] } = useTicketList(true);
  const usableTicket = usableTickets.find(
    (t: TicketListItem) => t.purchaseTicketSeq === purchaseTicketSeq,
  );
  // 예약에 쓸 반려견: 이용권에 귀속된 반려견 > 진입 화면에서 선택돼 있던 반려견
  const reservePetSeq = usableTicket?.petSeq || petSeq || 0;

  const goReserve = () => {
    const companySeq = ticketDetail?.companySeq ?? usableTicket?.companySeq;
    if (!companySeq || !purchaseTicketSeq) return;
    history.push(
      `/customer/layout/reservation-with-subscription?companySeq=${companySeq}` +
        `&purchaseTicketSeq=${purchaseTicketSeq}` +
        (reservePetSeq ? `&petSeq=${reservePetSeq}` : ""),
    );
  };
  const [isRefundConfirmOpen, setIsRefundConfirmOpen] = useState(false);
  const [isRefundSuccessOpen, setIsRefundSuccessOpen] = useState(false);

  if (isDetailLoading || !purchaseTicketSeq) {
    return (
      <>
        <LayoutHeader
          title="이용권 상세 정보"
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex justify-center items-center flex-auto">
          <Loading visible={true} />
        </div>
      </>
    );
  }

  if (!ticketDetail) {
    return (
      <>
        <LayoutHeader
          title="이용권 상세 정보"
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex flex-col flex-auto bg-[#F5F5F5] px-4 pt-4">
          <div className="py-12 text-center text-[#666666]">
            이용권 정보를 불러올 수 없습니다.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader
        title="이용권 상세 정보"
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex flex-col flex-auto bg-[#F5F5F5] pb-24">
        <div className="px-4 pt-4 pb-6">
          <div className="bg-white rounded-xl border border-[#E9E9E9] overflow-hidden">
            <div className="flex flex-col gap-4 p-4">
              {/* 이용권 이름 */}
              <div className="flex flex-col gap-1">
                <div className="text-[13px] text-[#666666]">이용권명</div>
                <div className="text-[15px] font-medium text-[#333333]">
                  {ticketDetail.name}
                </div>
              </div>

              {/* 이용권 내용 */}
              {ticketDetail.contents && (
                <div className="flex flex-col gap-1">
                  <div className="text-[13px] text-[#666666]">이용권 내용</div>
                  <div className="text-[14px] text-[#333333]">
                    {ticketDetail.contents}
                  </div>
                </div>
              )}

              {/* 업체명 */}
              <div className="flex flex-col gap-1">
                <div className="text-[13px] text-[#666666]">업체명</div>
                <div className="text-[14px] text-[#333333]">
                  {ticketDetail.companyName}
                </div>
              </div>

              {/* 주소 */}
              {ticketDetail.address && (
                <div className="flex flex-col gap-1">
                  <div className="text-[13px] text-[#666666]">주소</div>
                  <div className="text-[14px] text-[#333333]">
                    {ticketDetail.address}
                  </div>
                </div>
              )}

              {/* 횟수/박수 정보
                  - 호텔: 박수 단위로 1회 사용 시 전량 소진 → "X박"만 표시
                  - 그 외: "N회 사용 (M회 남음)" 형식 (splitYn="N" 단일권도 카운트 보존되어 표시) */}
              {typeof ticketDetail.totalCount === "number" &&
                typeof ticketDetail.remainCount === "number" &&
                (ticketDetail.cdProductType === "PRODUCT_TYPE_HOTEL" ? (
                  <div className="flex flex-col gap-1">
                    <div className="text-[13px] text-[#666666]">이용 박수</div>
                    <div className="text-[14px] text-[#333333]">
                      {ticketDetail.totalCount}박
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <div className="text-[13px] text-[#666666]">이용 횟수</div>
                    <div className="text-[14px]">
                      <span className="text-[#333333]">
                        {Math.max(
                          0,
                          ticketDetail.totalCount - ticketDetail.remainCount,
                        )}
                        회 사용
                      </span>
                      <span
                        className={
                          ticketDetail.remainCount === 0
                            ? "ml-1 text-[#999999]"
                            : "ml-1 font-medium text-orange-500"
                        }
                      >
                        ({ticketDetail.remainCount}회 남음)
                      </span>
                    </div>
                  </div>
                ))}

              {/* 가격 정보 */}
              <div className="flex flex-col gap-2 py-3 px-3 mt-2 bg-[#F5F5F5] rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-[13px] text-[#666666]">정가</span>
                  <span className="text-[14px] font-medium text-[#333333]">
                    {ticketDetail.cost.toLocaleString()}원
                  </span>
                </div>
                {ticketDetail.discount > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-[13px] text-[#666666]">할인율</span>
                    <span className="text-[14px] font-medium text-orange-500">
                      {ticketDetail.discount}%
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-[#E9E9E9]">
                  <span className="text-[14px] font-semibold text-[#333333]">
                    결제 금액
                  </span>
                  <span className="text-[15px] font-bold text-orange-500">
                    {ticketDetail.amount.toLocaleString()}원
                  </span>
                </div>
              </div>

              {/* 구매일/시작일/만료일 */}
              <div className="flex flex-col gap-3 mt-2">
                <div className="flex flex-col gap-1">
                  <div className="text-[13px] text-[#666666]">구매일</div>
                  <div className="text-[14px] text-[#333333]">
                    {ticketDetail.purchaseDate}
                  </div>
                </div>
                {ticketDetail.usageDurationDays != null &&
                ticketDetail.dtFirstUse == null ? (
                  // 최초 이용일 방식: 아직 첫 이용 전 → 만료일 대신 안내 문구
                  <div className="flex flex-col gap-1">
                    <div className="text-[13px] text-[#666666]">유효기간</div>
                    <div className="text-[14px] text-[#333333]">
                      첫 이용일로부터 {ticketDetail.usageDurationDays}일 사용
                      가능
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    {ticketDetail.usableDate && (
                      <div className="flex flex-col flex-1 gap-1">
                        <div className="text-[13px] text-[#666666]">시작일</div>
                        <div className="text-[14px] text-[#333333]">
                          {ticketDetail.dtFirstUse ?? ticketDetail.usableDate}
                        </div>
                      </div>
                    )}
                    {ticketDetail.expiredDate && (
                      <div className="flex flex-col flex-1 gap-1">
                        <div className="text-[13px] text-[#666666]">만료일</div>
                        <div className="text-[14px] text-[#333333]">
                          {ticketDetail.expiredDate}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 예약하기: 예약에 사용할 수 있는 이용권일 때만 노출
              환불하기: refundable 값일 때만 노출 */}
          {(!!usableTicket ||
            (ticketDetail.isRefundable ?? ticketDetail.refundable)) && (
            <div className="flex flex-col gap-2 mt-4 px-4">
              {!!usableTicket && (
                <button
                  type="button"
                  onClick={goReserve}
                  className="w-full py-3 text-[14px] font-semibold text-white bg-orange-400 rounded-lg"
                >
                  이 이용권으로 예약하기
                </button>
              )}
              {(ticketDetail.isRefundable ?? ticketDetail.refundable) && (
                <button
                  type="button"
                  onClick={() => setIsRefundConfirmOpen(true)}
                  className="w-full py-3 text-[14px] font-semibold text-[#666666] bg-white border border-[#E9E9E9] rounded-lg"
                >
                  환불하기
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 환불 확인 모달 */}
      <Dialog
        visible={isRefundConfirmOpen}
        onClose={() => setIsRefundConfirmOpen(false)}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">환불 신청</div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              이용권 환불을 신청하시겠습니까?
            </div>
            <div className="text-sm text-center text-[#666666] mt-2">
              환불 규정에 따라 환불 금액이 결정됩니다.
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              type="button"
              className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full"
              onClick={() => setIsRefundConfirmOpen(false)}
              disabled={refundTicketMutation.isPending}
            >
              취소
            </button>
            <button
              type="button"
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={async () => {
                if (!purchaseTicketSeq) return;
                try {
                  await refundTicketMutation.mutateAsync({
                    purchaseTicketSeq,
                    reason: "고객 요청에 의한 환불",
                  });
                  setIsRefundConfirmOpen(false);
                  setIsRefundSuccessOpen(true);
                } catch {
                  // 에러는 mutation에서 throw, 토스트 등으로 처리 가능
                }
              }}
              disabled={refundTicketMutation.isPending}
            >
              {refundTicketMutation.isPending ? "처리 중..." : "확인"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* 환불 완료 모달 */}
      <Dialog
        visible={isRefundSuccessOpen}
        onClose={() => {
          setIsRefundSuccessOpen(false);
          history.push("/customer/layout/user-subscriptions");
        }}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              환불 신청 완료
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              환불 신청이 완료되었습니다.
            </div>
            <div className="text-sm text-center text-[#666666] mt-2">
              환불 처리는 영업일 기준 3-5일 소요됩니다.
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              type="button"
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={() => {
                setIsRefundSuccessOpen(false);
                history.push("/customer/layout/user-subscriptions");
              }}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
